import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { VipProgress, VipBadge } from '../components/common/VipBadge';
import api from '../utils/api';

export default function Profile() {
  const { user, updateUser } = useAuth();
  const [form, setForm] = useState({
    firstName: user?.profile?.firstName || '',
    lastName:  user?.profile?.lastName  || '',
    address:   user?.profile?.address   || '',
    zipCode:   user?.profile?.zipCode   || '',
    phone:     user?.profile?.phone     || '',
    telegram:  user?.profile?.telegram  || '',
    whatsapp:  user?.profile?.whatsapp  || '',
    facebook:  user?.profile?.facebook  || '',
  });
  const [avatar, setAvatar] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(user?.profile?.avatar || null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [pwForm, setPwForm] = useState({ currentPassword: '', newPassword: '', confirm: '' });
  const [pwMessage, setPwMessage] = useState(null);
  const [changingPw, setChangingPw] = useState(false);
  const [referralStats, setReferralStats] = useState(null);
  const [copyMsg, setCopyMsg] = useState('');
  const [myDeposits, setMyDeposits] = useState([]);
  const [myMembership, setMyMembership] = useState(null);

  useEffect(() => {
    api.get('/referrals/stats').then(({ data }) => setReferralStats(data)).catch(() => {});
    api.get('/deposits/my').then(({ data }) => setMyDeposits(data.deposits || [])).catch(() => {});
    api.get('/memberships').then(({ data }) => {
      const userId = user?.id || user?._id;
      const memberships = data.memberships || [];
      for (const m of memberships) {
        const app = m.applicants?.find(a => String(a.user) === String(userId) && a.status === 'approved');
        if (app) { setMyMembership(m); break; }
      }
    }).catch(() => {});
  }, []);

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setAvatar(file);
    setAvatarPreview(URL.createObjectURL(file));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true); setMessage(null);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (avatar) fd.append('avatar', avatar);
      const { data } = await api.put('/auth/profile', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      updateUser(data.user);
      setMessage({ type: 'success', text: '✅ Profile updated!' });
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.message || 'Error saving' });
    } finally { setSaving(false); }
  };

  const handlePwChange = async (e) => {
    e.preventDefault();
    if (pwForm.newPassword !== pwForm.confirm) return setPwMessage({ type: 'error', text: 'Passwords do not match' });
    setChangingPw(true); setPwMessage(null);
    try {
      await api.put('/auth/change-password', { currentPassword: pwForm.currentPassword, newPassword: pwForm.newPassword });
      setPwMessage({ type: 'success', text: '✅ Password changed!' });
      setPwForm({ currentPassword: '', newPassword: '', confirm: '' });
    } catch (err) {
      setPwMessage({ type: 'error', text: err.response?.data?.message || 'Error' });
    } finally { setChangingPw(false); }
  };

  const copyReferralLink = () => {
    const link = `${window.location.origin}/register?ref=${user?.referralCode}`;
    navigator.clipboard.writeText(link);
    setCopyMsg('Copied!');
    setTimeout(() => setCopyMsg(''), 2000);
  };

  const msgStyle = (type) => ({
    background: type === 'success' ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)',
    border: `1px solid ${type === 'success' ? 'rgba(16,185,129,0.3)' : 'rgba(239,68,68,0.3)'}`,
    borderRadius: 8, padding: '10px 14px', fontSize: 14,
    color: type === 'success' ? '#10b981' : '#ef4444', marginBottom: 16,
  });

  const totalDeposited = myDeposits.filter(d => d.status === 'verified').reduce((s, d) => s + (d.amount || 0), 0);

  return (
    <div style={{ paddingTop: 70 }}>
      <div className="page-header">
        <h1>MY PROFILE</h1>
        <p>Manage your account, VIP status, and referrals</p>
      </div>

      <div className="container" style={{ paddingBottom: 80, maxWidth: 1000 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: 24 }}>

          {/* Profile Form */}
          <div className="card" style={{ padding: 28 }}>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 16, marginBottom: 20, color: '#f5c400' }}>PROFILE INFO</h2>
            {message && <div style={msgStyle(message.type)}>{message.text}</div>}
            <form onSubmit={handleSave}>
              {/* Avatar upload */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 }}>
                <div style={{ width: 72, height: 72, borderRadius: '50%', background: 'var(--gradient-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--font-display)', fontWeight: 900, fontSize: 28, color: '#0a0900', overflow: 'hidden', flexShrink: 0, border: '2px solid var(--border-glow)' }}>
                  {avatarPreview
                    ? <img src={avatarPreview} alt="avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    : user?.username?.[0]?.toUpperCase()}
                </div>
                <div>
                  <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 6 }}>Profile Picture</div>
                  <input type="file" accept="image/*" onChange={handleAvatarChange} style={{ fontSize: 13 }} />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="form-group">
                  <label>First Name</label>
                  <input value={form.firstName} onChange={e => setForm({ ...form, firstName: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Last Name</label>
                  <input value={form.lastName} onChange={e => setForm({ ...form, lastName: e.target.value })} />
                </div>
              </div>
              <div className="form-group"><label>Address</label><input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} /></div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="form-group"><label>ZIP Code</label><input value={form.zipCode} onChange={e => setForm({ ...form, zipCode: e.target.value })} /></div>
                <div className="form-group"><label>Phone</label><input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} /></div>
              </div>
              <hr className="divider" />
              <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12, fontWeight: 600, letterSpacing: '0.05em' }}>OPTIONAL CONTACTS</p>
              <div className="form-group"><label>Telegram</label><input placeholder="@username" value={form.telegram} onChange={e => setForm({ ...form, telegram: e.target.value })} /></div>
              <div className="form-group"><label>WhatsApp</label><input placeholder="+1234567890" value={form.whatsapp} onChange={e => setForm({ ...form, whatsapp: e.target.value })} /></div>
              <div className="form-group"><label>Facebook</label><input placeholder="Profile URL" value={form.facebook} onChange={e => setForm({ ...form, facebook: e.target.value })} /></div>
              <button type="submit" className="btn btn-primary" style={{ width: '100%' }} disabled={saving}>{saving ? 'Saving...' : 'Save Changes'}</button>
            </form>
          </div>

          {/* Right column */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

            {/* Account Info */}
            <div className="card" style={{ padding: 24 }}>
              <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 16, marginBottom: 16, color: '#f5c400' }}>ACCOUNT</h2>
              {[['Username', user?.username], ['Email', user?.email], ['Role', user?.role?.replace('_', ' ').toUpperCase()], ['Member Since', new Date(user?.createdAt).toLocaleDateString()], ['Active Membership', myMembership ? myMembership.name : 'None'], myMembership && ['Membership Expires', user?.membershipExpiry ? new Date(user.membershipExpiry).toLocaleDateString() : 'N/A']].filter(Boolean).map(([label, val]) => (
                <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
                  <span style={{ color: 'var(--text-secondary)', fontSize: 14 }}>{label}</span>
                  <span style={{ color: 'var(--text-primary)', fontWeight: 600, fontSize: 14 }}>{val}</span>
                </div>
              ))}
            </div>

            {/* VIP Status */}
            <div className="card" style={{ padding: 24 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 16, color: '#f5c400' }}>VIP STATUS</h2>
                <VipBadge tier={user?.vipTier || 'bronze'} size="md" />
              </div>
              <VipProgress totalDeposited={totalDeposited} />
              <div style={{ marginTop: 12, fontSize: 12, color: 'var(--text-muted)' }}>
                Based on your total verified deposits: <strong style={{ color: '#f5c400' }}>${totalDeposited.toLocaleString()}</strong>
              </div>
            </div>

            {/* Referral Section */}
            <div className="card" style={{ padding: 24 }}>
              <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 16, marginBottom: 16, color: '#f5c400' }}>REFERRAL PROGRAM</h2>
              <div style={{ background: 'var(--bg-secondary)', borderRadius: 10, padding: 14, marginBottom: 14 }}>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 6 }}>YOUR REFERRAL LINK</div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <div style={{ flex: 1, fontSize: 12, color: '#f5c400', fontFamily: 'monospace', background: 'var(--bg-card)', padding: '8px 12px', borderRadius: 6, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {window.location.origin}/register?ref={user?.referralCode}
                  </div>
                  <button onClick={copyReferralLink} className="btn btn-primary btn-sm" style={{ flexShrink: 0 }}>
                    {copyMsg || 'Copy'}
                  </button>
                </div>
              </div>
              {referralStats && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
                  {[['Total', referralStats.totalReferrals], ['Completed', referralStats.completed], ['Bonus FC', `$${referralStats.totalBonus}`]].map(([k, v]) => (
                    <div key={k} style={{ background: 'var(--bg-secondary)', borderRadius: 8, padding: '10px', textAlign: 'center' }}>
                      <div style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 900, color: '#f5c400' }}>{v}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{k}</div>
                    </div>
                  ))}
                </div>
              )}
              <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 12 }}>
                Share your link — when someone signs up using it, both of you get bonus Fun Credits! 🎉
              </p>
            </div>

            {/* Change Password */}
            <div className="card" style={{ padding: 24 }}>
              <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 16, marginBottom: 16, color: '#f5c400' }}>CHANGE PASSWORD</h2>
              {pwMessage && <div style={msgStyle(pwMessage.type)}>{pwMessage.text}</div>}
              <form onSubmit={handlePwChange}>
                <div className="form-group"><label>Current Password</label><input type="password" value={pwForm.currentPassword} onChange={e => setPwForm({ ...pwForm, currentPassword: e.target.value })} required /></div>
                <div className="form-group"><label>New Password</label><input type="password" value={pwForm.newPassword} onChange={e => setPwForm({ ...pwForm, newPassword: e.target.value })} required /></div>
                <div className="form-group"><label>Confirm New Password</label><input type="password" value={pwForm.confirm} onChange={e => setPwForm({ ...pwForm, confirm: e.target.value })} required /></div>
                <button type="submit" className="btn btn-secondary" style={{ width: '100%' }} disabled={changingPw}>{changingPw ? 'Changing...' : 'Change Password'}</button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
